import React, { useEffect, useState } from "react";
import { Loader2, Save, Box as BoxIcon, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const inputCls = "w-full bg-white border border-slate-300 rounded-md px-3 py-2 text-sm text-slate-900 focus:border-spike-blue focus:ring-2 focus:ring-spike-blue/20 outline-none";

const AdminBoxConfig = () => {
  const [venues, setVenues] = useState([]);
  const [venueId, setVenueId] = useState("");
  const [venue, setVenue] = useState(null);
  const [lanesPerBox, setLanesPerBox] = useState(3);
  const [excluded, setExcluded] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/venues", { withCredentials: true });
        setVenues(data);
        if (data.length) setVenueId(data[0].id);
      } catch {
        toast.error("Could not load venues.");
      }
    })();
  }, []);

  useEffect(() => {
    if (!venueId) return;
    const v = venues.find((x) => x.id === venueId);
    if (v) {
      setVenue(v);
      setLanesPerBox(v.box_config?.lanes_per_box ?? 3);
      setExcluded(v.box_config?.excluded_lanes ?? []);
      setLoading(false);
    }
  }, [venueId, venues]);

  const toggleLane = (n) => {
    setExcluded((cur) => cur.includes(n) ? cur.filter((x) => x !== n) : [...cur, n].sort((a, b) => a - b));
  };

  const save = async () => {
    if (!venue) return;
    setSaving(true);
    try {
      await api.patch(`/admin/venues/${venueId}`, {
        name: venue.name,
        city: venue.city,
        address: venue.address,
        phone: venue.phone,
        email: venue.email,
        status: venue.status,
        lanes: venue.lanes,
        sports: venue.sports,
        machines: venue.machines,
        image: venue.image,
        lane_price_cad: venue.lane_price_cad,
        box_price_cad: venue.box_price_cad,
        box_config: { lanes_per_box: Number(lanesPerBox), excluded_lanes: excluded },
      }, { withCredentials: true });
      toast.success("Box configuration saved.");
      // refresh
      const { data } = await api.get("/admin/venues", { withCredentials: true });
      setVenues(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  // Compute preview box groups
  const previewGroups = () => {
    if (!venue) return [];
    const lpb = Number(lanesPerBox);
    const total = venue.lanes;
    const exSet = new Set(excluded);
    const out = [];
    let n = 1;
    while (n + lpb - 1 <= total) {
      const g = [];
      for (let i = 0; i < lpb; i++) g.push(n + i);
      if (!g.some((x) => exSet.has(x))) { out.push(g); n += lpb; } else { n += 1; }
    }
    return out;
  };

  if (loading || !venue) return <div className="py-12 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-spike-blue" /></div>;

  const groups = previewGroups();

  return (
    <div className="space-y-5" data-testid="admin-box-config">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
            <BoxIcon className="w-5 h-5 text-spike-blue" /> Box Configuration
          </h2>
          <p className="text-sm text-slate-600 mt-1">Set lanes per box and mark lanes under maintenance.</p>
        </div>
        <select data-testid="bx-venue-select" value={venueId} onChange={(e) => setVenueId(e.target.value)} className={inputCls + " w-auto"}>
          {venues.map((v) => (<option key={v.id} value={v.id}>{v.name}</option>))}
        </select>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-card space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1.5">Lanes per box</label>
            <input
              type="number"
              min={1}
              max={venue.lanes}
              data-testid="bx-lpb-input"
              className={inputCls + " w-32"}
              value={lanesPerBox}
              onChange={(e) => setLanesPerBox(e.target.value)}
            />
            <p className="text-xs text-slate-500 mt-1">Total lanes at venue: {venue.lanes}. A box = N consecutive available lanes.</p>
          </div>
          <div>
            <div className="text-xs font-medium text-slate-700 mb-1.5">Box price (CAD / hour)</div>
            <div className="text-lg font-semibold text-slate-900">${venue.box_price_cad}</div>
            <p className="text-xs text-slate-500">Edit in Venues admin →</p>
          </div>
        </div>

        <div>
          <div className="text-xs font-medium text-slate-700 mb-2">Lanes (click to toggle maintenance)</div>
          <div className="grid grid-cols-4 md:grid-cols-8 gap-2" data-testid="bx-lanes-grid">
            {Array.from({ length: venue.lanes }, (_, i) => {
              const n = i + 1;
              const isOut = excluded.includes(n);
              return (
                <button
                  key={n}
                  type="button"
                  data-testid={`bx-lane-${n}`}
                  onClick={() => toggleLane(n)}
                  className={`px-3 py-4 rounded-md border-2 transition-all text-center ${
                    isOut
                      ? "border-red-300 bg-red-50 text-red-700"
                      : "border-emerald-300 bg-emerald-50 text-emerald-700 hover:border-emerald-400"
                  }`}
                >
                  <div className="text-xs uppercase tracking-wider opacity-70">Lane</div>
                  <div className="font-semibold text-lg mt-0.5">{n}</div>
                  <div className="text-xs mt-1 opacity-80">{isOut ? "Maintenance" : "Available"}</div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="bg-slate-50 border border-slate-200 rounded-md p-4" data-testid="bx-preview">
          <div className="text-xs font-medium text-slate-700 mb-2">Preview · Box groups</div>
          <div className="flex flex-wrap gap-2">
            {groups.map((g, i) => (
              <span key={i} className="px-3 py-1.5 bg-spike-blue-pale text-spike-blue text-sm font-medium rounded">
                Box {String.fromCharCode(65 + i)} · lanes {g.join(", ")}
              </span>
            ))}
            {groups.length === 0 && (
              <span className="inline-flex items-center gap-2 text-amber-700 text-sm">
                <AlertTriangle className="w-4 h-4" />
                No valid box groups with current configuration.
              </span>
            )}
          </div>
        </div>

        <button type="button" data-testid="bx-save" onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-spike-blue hover:bg-spike-blue-hover disabled:opacity-50 text-white text-sm font-medium px-5 py-2.5 rounded-md">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save configuration
        </button>
      </div>
    </div>
  );
};

export default AdminBoxConfig;
